/**
 * Access control for the internal Security Posture page (`/dev/security`).
 *
 * Phase 1 (now): only "system developers" — an explicit allowlist of emails
 * sourced from the build-time env var `VITE_SYSTEM_DEV_EMAILS` (comma-
 * separated). This is intentionally a small, hardcoded list rather than a
 * role flag because the page is meant to surface internal posture
 * information that admins do not yet need.
 *
 * Phase 2 (later): flip `VITE_SECURITY_PAGE_ADMINS_ENABLED=true` and
 * organization admins (AuthRole.isAdmin === true) will also see it.
 *
 * NOTE: This is a client-side gate. It hides the UI but is not by itself
 * a security boundary — anything truly sensitive must also be gated server-
 * side. Today this page renders only static, in-repo posture content, not
 * secrets, so the client gate is sufficient for its purpose.
 */

import type { AuthRole, AuthUser } from '../api/auth.service.js';

const SYSTEM_DEV_EMAILS_ENV = (import.meta.env.VITE_SYSTEM_DEV_EMAILS ?? '') as string;
const ADMIN_ACCESS_ENABLED_ENV = (import.meta.env.VITE_SECURITY_PAGE_ADMINS_ENABLED ?? '') as string;

function parseEmailList(raw: string): string[] {
  return raw
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);
}

const SYSTEM_DEV_EMAILS: ReadonlySet<string> = new Set(parseEmailList(SYSTEM_DEV_EMAILS_ENV));

const ADMINS_ENABLED: boolean = ADMIN_ACCESS_ENABLED_ENV.toLowerCase() === 'true';

export function isSystemDev(user: AuthUser | null | undefined): boolean {
  if (!user?.email) return false;
  return SYSTEM_DEV_EMAILS.has(user.email.toLowerCase());
}

export function canViewSecurityPage(
  user: AuthUser | null | undefined,
  role: AuthRole | null | undefined,
): boolean {
  if (isSystemDev(user)) return true;
  if (ADMINS_ENABLED && role?.isAdmin === true) return true;
  return false;
}

export const securityPageAccessConfig = {
  systemDevCount: SYSTEM_DEV_EMAILS.size,
  adminsEnabled: ADMINS_ENABLED,
} as const;
